import styled from '@emotion/styled';

/**
 * Returns a styled symbol component based on the specified type.
 *
 * @param type - The type of symbol to render ('circle', 'diamond', 'square', 'line', or 'area')
 * @param color - The primary color used for borders and lines
 * @param fillColor - Optional fill color for the symbol interior (defaults to `color` if not provided)
 * @param lineIsDashed - Optional flag to render the symbol with a dashed stroke
 * @param lineWidth - The width of the stroke/border in pixels (defaults to 2)
 * @returns A React element representing the symbol, or `null` if the type is not recognized
 */
export const getSymbol = (
  type: 'circle' | 'diamond' | 'square' | 'line' | 'area',
  color: string,
  fillColor?: string,
  lineIsDashed?: boolean,
  lineWidth = 2,
) => {
  switch (type) {
    case 'circle':
      return (
        <CircleSymbol
          fillColor={fillColor || color}
          borderColor={color}
          lineWidth={lineWidth}
          lineIsDashed={lineIsDashed}
        />
      );
    case 'diamond':
      return (
        <DiamondSymbol
          fillColor={fillColor || color}
          borderColor={color}
          lineWidth={lineWidth}
          lineIsDashed={lineIsDashed}
        />
      );
    case 'square':
      return (
        <SquareSymbol
          fillColor={fillColor || color}
          borderColor={color}
          lineWidth={lineWidth}
          lineIsDashed={lineIsDashed}
        />
      );
    case 'line':
      return <LineSymbol color={color} lineWidth={lineWidth} lineIsDashed={lineIsDashed} />;
    case 'area':
      return (
        <AreaSymbol
          fillColor={fillColor || color}
          borderColor={color}
          lineWidth={lineWidth}
          lineIsDashed={lineIsDashed}
        />
      );
    default:
      return null;
  }
};

interface SymbolProps {
  fillColor?: string;
  borderColor?: string;
  color?: string;
  lineWidth?: number;
  lineIsDashed?: boolean;
}

const CircleSymbol = styled.div<SymbolProps>`
    width: 12px;
    height: 12px;
    background-color: ${({ fillColor }) => fillColor};
    border-width: ${({ lineWidth }) => lineWidth}px;
    border-color: ${({ borderColor }) => borderColor};
    border-style: ${({ lineIsDashed }) => (lineIsDashed ? 'dashed' : 'solid')};
    border-radius: 50%;
`;

const SquareSymbol = styled.div<SymbolProps>`
    width: 12px;
    height: 12px;
    background-color: ${({ fillColor }) => fillColor};
    border-width: ${({ lineWidth }) => lineWidth}px;
    border-color: ${({ borderColor }) => borderColor};
    border-style: ${({ lineIsDashed }) => (lineIsDashed ? 'dashed' : 'solid')};
`;

// A square turned 45°, shrunk so its corners span the same 12px as the other symbols.
const DiamondSymbol = styled(SquareSymbol)`
    width: 8px;
    height: 8px;
    margin: 2px;
    transform: rotate(45deg);
`;

const LineSymbol = styled.div<SymbolProps>`
    width: 12px;
    height: ${({ lineWidth }) => lineWidth}px;
    background-color: transparent;
    border-top: ${({ lineWidth, lineIsDashed, color }) =>
      `${lineWidth}px ${lineIsDashed ? 'dashed' : 'solid'} ${color}`};
`;

const AreaSymbol = styled.div<SymbolProps>`
    width: 12px;
    height: 12px;
    background-color: ${({ fillColor }) => fillColor};
    border: ${({ lineWidth, lineIsDashed, borderColor }) =>
      `${lineWidth}px ${lineIsDashed ? 'dashed' : 'solid'} ${borderColor}`};
`;
